import {FilterMatchMode, FilterOperator} from "primereact/api";
import {
    ADD_AUDIO_CONTROL_BLOCK,
    ADD_AUDIO_CONTROL_CONDITION,
    ARCHIVE_AUDIO_CONTROL_TABLE_TOGGLE,
    AUDIO_CONTROL_CLEAR_FILTERS,
    AUDIO_CONTROL_DIALOG_VISIBLE,
    AUDIO_CONTROL_FIELD,
    GET_AUDIO_CONTROL,
    GET_AUDIO_CONTROL_PAGINATION,
    GET_AUDIO_CONTROLS,
    INITIAL_AUDIO_CONTROL_DATA,
    REMOVE_AUDIO_CONTROL_BLOCK,
    REMOVE_AUDIO_CONTROL_CONDITION,
    SELECTED_AUDIO_CONTROL_ID,
    SET_AUDIO_CONTROL_BLOCKS,
    SET_AUDIO_CONTROL_CURRENT_PAGE,
    SET_AUDIO_CONTROL_CURRENT_SIZE,
    SET_AUDIO_CONTROL_ERROR_FIELDS,
    SET_FAIL_LIST,
    SHOW_AUDIO_CONTROL_DETAIL_LOADING,
    SHOW_AUDIO_CONTROL_LOADER,
    SHOW_AUDIO_CONTROL_SAVE_LOADER,
    UPDATE_AUDIO_CONTROL_BLOCK,
    UPDATE_AUDIO_CONTROL_CONDITION,
} from "../types/typesAudioControlList";
import {
    applyInactiveAudioControlBlock,
    applyInactiveAudioControlCondition,
    createEmptyAudioControlBlock,
    createEmptyFailListItem,
    isAudioControlBlockActive,
    normalizeAudioControlBlocks,
    normalizeFailList,
    normalizeQualityControlMetadata,
    type AudioControlBlock,
    type AudioControlData,
    type AudioControlErrorFields,
} from "../../components/Settings/AudioControlList/audioControlQuestionnaire";

const initialAudioControlData: AudioControlData = {
    id: null,
    name: null,
    description: null,
    branchId: null,
    mainBranchId: null,
    isActive: false,
    isArchived: false,
    failList: [createEmptyFailListItem()],
    blocks: [createEmptyAudioControlBlock()],
    qualityControlData: null,
};

const normalizeAudioControlData = (payload: unknown): AudioControlData => {
    if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
        return initialAudioControlData;
    }
    const row = payload as Record<string, unknown>;
    const blocks = normalizeAudioControlBlocks(row.blocks ?? row.models);

    return {
        id: (row.id as number | null | undefined) ?? null,
        name: (row.name as string | null | undefined) ?? null,
        description: (row.description as string | null | undefined) ?? null,
        branchId: (row.branchId as number | null | undefined) ?? null,
        mainBranchId: (row.mainBranchId as number | null | undefined) ?? null,
        isActive: Boolean(row.isActive),
        isArchived: Boolean(row.isArchived),
        qualityControlData: normalizeQualityControlMetadata(row.qualityControlData),
        failList: normalizeFailList(row.failList ?? row.questionnaireFailConditions),
        blocks: blocks.length ? blocks : [createEmptyAudioControlBlock()],
    };
};

export type AudioControlListState = {
    archiveAudioControlTableToggle: boolean;
    audioControlDialogVisible: boolean;
    selectedAudioControlId: number | null;
    clearFilters: {
        id: { operator: typeof FilterOperator.AND; constraints: { value: null; matchMode: typeof FilterMatchMode.CONTAINS }[] };
        name: { operator: typeof FilterOperator.AND; constraints: { value: null; matchMode: typeof FilterMatchMode.CONTAINS }[] };
        isActive: { value: null; matchMode: typeof FilterMatchMode.IN };
    };
    audioControlsList: unknown[];
    pagination: {
        countPage: number;
        totalItems: number;
    };
    paginationPage: number;
    paginationSize: number;
    audioControlData: AudioControlData;
    loading: boolean;
    detailsLoading: boolean;
    saveLoader: boolean;
    errorFields: AudioControlErrorFields;
};

const initialState: AudioControlListState = {
    archiveAudioControlTableToggle: true,
    audioControlDialogVisible: false,
    selectedAudioControlId: null,
    clearFilters: {
        id: {operator: FilterOperator.AND, constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
        name: {operator: FilterOperator.AND, constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
        isActive: {value: null, matchMode: FilterMatchMode.IN},
    },
    audioControlsList: [],
    pagination: {
        countPage: 0,
        totalItems: 0,
    },
    paginationPage: 1,
    paginationSize: 10,
    audioControlData: initialAudioControlData,
    loading: false,
    detailsLoading: false,
    saveLoader: false,
    errorFields: null,
}

const updateBlockAtIndex = (
    blocks: AudioControlBlock[],
    blockIndex: number,
    updater: (block: AudioControlBlock) => AudioControlBlock
): AudioControlBlock[] =>
    blocks.map((block, currentBlockIndex) => (currentBlockIndex === blockIndex ? updater(block) : block));

export const AudioControlListReducer = (
    state: AudioControlListState = initialState,
    action: { type: string; payload?: any },
): AudioControlListState => {
    switch (action.type) {
        case ARCHIVE_AUDIO_CONTROL_TABLE_TOGGLE:
            return {...state, archiveAudioControlTableToggle: action.payload}
        case AUDIO_CONTROL_DIALOG_VISIBLE:
            return {...state, audioControlDialogVisible: action.payload}
        case SELECTED_AUDIO_CONTROL_ID:
            return {...state, selectedAudioControlId: action.payload}
        case AUDIO_CONTROL_FIELD:
            return {
                ...state,
                audioControlData: {
                    ...state.audioControlData,
                    [action.payload.fieldName]: action.payload.value,
                },
            }
        case SET_FAIL_LIST:
            return {
                ...state,
                audioControlData: {
                    ...state.audioControlData,
                    failList: action.payload,
                },
            }
        case SET_AUDIO_CONTROL_BLOCKS:
            return {
                ...state,
                audioControlData: {
                    ...state.audioControlData,
                    blocks: action.payload,
                },
            }
        case ADD_AUDIO_CONTROL_BLOCK:
            return {
                ...state,
                audioControlData: {
                    ...state.audioControlData,
                    blocks: [...(state.audioControlData.blocks ?? []), action.payload],
                },
            }
        case REMOVE_AUDIO_CONTROL_BLOCK: {
            const blocks = [...(state.audioControlData.blocks ?? [])];
            if (blocks.length <= 1) {
                return state;
            }
            blocks.splice(action.payload, 1);
            return {
                ...state,
                audioControlData: {
                    ...state.audioControlData,
                    blocks,
                },
            }
        }
        case UPDATE_AUDIO_CONTROL_BLOCK: {
            const {blockIndex, fieldName, value} = action.payload;
            return {
                ...state,
                audioControlData: {
                    ...state.audioControlData,
                    blocks: updateBlockAtIndex(state.audioControlData.blocks ?? [], blockIndex, (block) => {
                        if (fieldName === 'isActive' && value === false) {
                            return applyInactiveAudioControlBlock(block);
                        }

                        return {
                            ...block,
                            [fieldName]: value,
                        };
                    }),
                },
            }
        }
        case ADD_AUDIO_CONTROL_CONDITION: {
            const {blockIndex, condition} = action.payload;
            return {
                ...state,
                audioControlData: {
                    ...state.audioControlData,
                    blocks: updateBlockAtIndex(state.audioControlData.blocks ?? [], blockIndex, (block) => {
                        const nextCondition = !isAudioControlBlockActive(block)
                            ? applyInactiveAudioControlCondition(condition)
                            : condition;

                        return {
                            ...block,
                            conditions: [...(block.conditions ?? []), nextCondition],
                        };
                    }),
                },
            }
        }
        case REMOVE_AUDIO_CONTROL_CONDITION: {
            const {blockIndex, conditionIndex} = action.payload;
            return {
                ...state,
                audioControlData: {
                    ...state.audioControlData,
                    blocks: updateBlockAtIndex(state.audioControlData.blocks ?? [], blockIndex, (block) => {
                        const conditions = [...(block.conditions ?? [])];
                        if (conditions.length <= 1) {
                            return block;
                        }
                        conditions.splice(conditionIndex, 1);
                        return {...block, conditions};
                    }),
                },
            }
        }
        case UPDATE_AUDIO_CONTROL_CONDITION: {
            const {blockIndex, conditionIndex, fieldName, value} = action.payload;
            return {
                ...state,
                audioControlData: {
                    ...state.audioControlData,
                    blocks: updateBlockAtIndex(state.audioControlData.blocks ?? [], blockIndex, (block) => ({
                        ...block,
                        conditions: (block.conditions ?? []).map((condition, currentConditionIndex) => {
                            if (currentConditionIndex !== conditionIndex) {
                                return condition;
                            }

                            const updatedCondition = {
                                ...condition,
                                [fieldName]: value,
                            };

                            if (fieldName === 'isActive' && value === false) {
                                updatedCondition.weight = 0;
                            }

                            return updatedCondition;
                        }),
                    })),
                },
            }
        }
        case AUDIO_CONTROL_CLEAR_FILTERS:
            return {
                ...state, clearFilters: {
                    id: {operator: FilterOperator.AND, constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
                    name: {operator: FilterOperator.AND, constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]},
                    isActive: {value: null, matchMode: FilterMatchMode.IN},
                }
            }
        case GET_AUDIO_CONTROLS:
            return {...state, audioControlsList: action.payload}
        case GET_AUDIO_CONTROL_PAGINATION:
            return {...state, pagination: {countPage: action.payload.countPage, totalItems: action.payload.totalItems}}
        case SET_AUDIO_CONTROL_CURRENT_PAGE:
            return {...state, paginationPage: action.payload}
        case SET_AUDIO_CONTROL_CURRENT_SIZE:
            return {...state, paginationSize: action.payload}
        case GET_AUDIO_CONTROL:
            return {...state, audioControlData: normalizeAudioControlData(action.payload)}
        case SHOW_AUDIO_CONTROL_LOADER:
            return {...state, loading: action.payload}
        case SHOW_AUDIO_CONTROL_DETAIL_LOADING:
            return {...state, detailsLoading: action.payload}
        case SHOW_AUDIO_CONTROL_SAVE_LOADER:
            return {...state, saveLoader: action.payload}
        case INITIAL_AUDIO_CONTROL_DATA:
            return {...state, audioControlData: initialAudioControlData}
        case SET_AUDIO_CONTROL_ERROR_FIELDS:
            return {...state, errorFields: action.payload}
        default:
            return state
    }
}
